import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import type { AgentDefinition, DefinitionView } from '../../Services/agents';
import { ActionsEditor } from './ActionsEditor';
import { RequirementsEditor } from './RequirementsEditor';
import { kindLabel, scheduleLabel } from './orgHelpers';

interface Props {
	detail: DefinitionView;
	saving: boolean;
	onSave: (def: AgentDefinition) => void;
	onReset: () => void;
}

// #region DefinitionEditor
// La définition d'un agent (niveau 2) : mission, consignes de plan, actions par défaut.
// Enregistrer crée une version ; « Revenir au code » supprime toutes les versions.
export const DefinitionEditor: React.FC<Props> = ({
	detail,
	saving,
	onSave,
	onReset,
}) => {
	const [def, setDef] = useState<AgentDefinition>(detail.definition);
	useEffect(() => setDef(detail.definition), [detail]);
	const { role } = detail;
	const dirty = JSON.stringify(def) !== JSON.stringify(detail.definition);

	return (
		<Stack spacing={2}>
			<RoleHeader detail={detail} />
			<TextField
				label="Mission (prompt système du rôle)"
				value={def.mission}
				onChange={(e) => setDef({ ...def, mission: e.target.value })}
				multiline
				minRows={4}
				fullWidth
			/>
			{role.kind === 'specialist' && (
				<>
					<TextField
						label="Consignes de planification"
						helperText="Ce que le planificateur doit respecter en traduisant les actions en KPI sur les données du client."
						value={def.planning_hints}
						onChange={(e) => setDef({ ...def, planning_hints: e.target.value })}
						multiline
						minRows={3}
						fullWidth
					/>
					<Typography variant="subtitle2">Données attendues</Typography>
					<Typography variant="caption" color="text.secondary">
						Rien n'est bloquant : elles orientent le planificateur, qui dit au
						client ce qui lui manque et quoi brancher. Le client les voit
						cochées selon ses connecteurs actifs.
					</Typography>
					<RequirementsEditor
						requirements={def.requirements || []}
						onChange={(requirements) => setDef({ ...def, requirements })}
					/>
					<Typography variant="subtitle2">Actions par défaut</Typography>
					<ActionsEditor
						actions={def.actions || []}
						onChange={(actions) => setDef({ ...def, actions })}
					/>
				</>
			)}
			{role.kind === 'manager' && (
				<Alert severity="info">
					Un manager n'a pas d'actions : il lit les recommandations de son
					équipe et synthétise.
				</Alert>
			)}
			<Stack direction="row" spacing={1}>
				<Button
					variant="contained"
					disabled={!dirty || saving}
					onClick={() => onSave(def)}
				>
					Enregistrer une nouvelle version
				</Button>
				<Button
					variant="outlined"
					color="warning"
					disabled={detail.source !== 'admin' || saving}
					onClick={onReset}
				>
					Revenir aux valeurs du code
				</Button>
			</Stack>
			<VersionsList detail={detail} />
		</Stack>
	);
};
// #endregion

// #region RoleHeader
const RoleHeader: React.FC<{ detail: DefinitionView }> = ({ detail }) => {
	const { role, source, version } = detail;
	return (
		<Box>
			<Typography variant="h6">{role.name}</Typography>
			<Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
				<Chip size="small" label={kindLabel(role.kind)} />
				<Chip size="small" label={scheduleLabel(role.schedule)} />
				{role.internal && <Chip size="small" label="mesure interne" />}
				<Chip
					size="small"
					color={source === 'admin' ? 'secondary' : 'default'}
					label={
						source === 'admin' ? `version admin ${version}` : 'valeurs du code'
					}
				/>
			</Stack>
			<Typography
				variant="caption"
				color="text.secondary"
				display="block"
				sx={{ mt: 0.5 }}
			>
				Slug <code>{role.slug}</code>
				{role.parent ? ` · rattaché à ${role.parent}` : ' · racine'}. La
				structure (liens, cadence) est dans le code ; les prérequis se règlent
				ci-dessous.
			</Typography>
		</Box>
	);
};
// #endregion

// #region VersionsList
const VersionsList: React.FC<{ detail: DefinitionView }> = ({ detail }) =>
	detail.versions.length === 0 ? null : (
		<Box>
			<Typography variant="subtitle2" gutterBottom>
				Historique
			</Typography>
			{detail.versions.map((v) => (
				<Typography key={v.id} variant="body2" color="text.secondary">
					v{v.version} · {new Date(v.created_at).toLocaleString()} ·{' '}
					{v.updated_by || 'admin'} · {v.actions?.length ?? 0} action(s)
				</Typography>
			))}
		</Box>
	);
// #endregion
